import { useEffect, useState } from 'react';
import { useOutletContext } from 'react-router-dom';
import type { ApiClient, AussenBerechnungslauf, AussenRueckhaltungszeile } from '../api';
import { bewertungname, herkunftname } from '../aussenKlartext';
import {
  ContentSection, DataTable, EmptyState, ErrorState, KpiCard, KpiReihe, LoadingState, StatusBadge,
  formatiereZahl, type Fachstatus, type Spalte, type Vorbehalt,
} from '../muster';
import { Rechenstand, aussenFachvorbehalt, zeitpunkt } from './sanitaer/kopf';
import { OHNE_LAUF, berichtZiel, useAussenKopf, useBerichtsstand } from './aussenanlagen/kopf';
import type { AussenanlagenKontext } from './Aussenanlagen';
import './aussenanlagen/aussen.css';

/**
 * ═══════════════════════════════════════════════════════════════════════════
 * DIE RÜCKHALTUNG NACH DWA-A 117 (Paket TS9b2, TD3)
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * Ein Reiter der Außenanlagen (Entscheidung E1: kein eigenes `<h1>`, der Kopf
 * gehört dem Rahmen). Je Regenwasser-Einleitstelle: vorhandenes Volumen,
 * Drosselabfluss, Obergrenze der Fläche, das erforderliche Volumen mit und
 * ohne Drossel, die Deckung und die Bewertung.
 *
 * Die Rückhaltung ist KEIN eigener Lauf: Sie entsteht mit dem
 * Versickerungslauf (`zeile.Bereich rueckhaltung`), ihr Bericht ist VER und
 * ihre Excel-Mappe die Versickerungsmappe. Rechenstand, PDF und Excel im Kopf
 * sind deshalb die des Versickerungslaufs - und der Vorbehalt AUA-01/02
 * (`aussenFachvorbehalt('versickerung')`) ist derselbe Satz wie in der Mappe.
 *
 * Der Drosselabfluss bleibt leer, solange die untere Wasserbehörde nicht
 * entschieden hat (F84, AUA-02). Ein leeres Feld sähe aus wie ein
 * Pflegerückstand; die Seite sagt deshalb „offen“ und nennt den Grund.
 */

/** Die Bewertungen der Rückhaltung als Marke - ein Mangel nur, wo geprüft und nicht gedeckt ist. */
const BEWERTUNG: Record<string, Fachstatus> = {
  ok: { zustand: 'gut', text: 'in Ordnung' },
  unterdeckt: { zustand: 'fehler', text: 'unterdeckt' },
  drossel_offen: { zustand: 'warnung', text: 'Drossel offen' },
  rueckhalteraum_offen: { zustand: 'warnung', text: 'Rückhalteraum offen' },
  nicht_bestimmbar: { zustand: 'warnung', text: 'nicht bestimmbar' },
  ohne_dauerstufen: { zustand: 'warnung', text: 'ohne Dauerstufen' },
  ohne_speicher: { zustand: 'warnung', text: 'ohne Speicher' },
  bezug_widerrufen: { zustand: 'neutral', text: 'Bezug widerrufen' },
};

export function rueckhaltungStatus(bewertung: string | null | undefined): Fachstatus {
  if (!bewertung) return { zustand: 'neutral', text: 'nicht bewertet' };
  return BEWERTUNG[bewertung] ?? { zustand: 'warnung', text: bewertungname(bewertung) };
}

/** Der Grund an einer offenen Drossel - die Herkunft sagt, worauf gewartet wird. */
function drosselGrund(z: AussenRueckhaltungszeile): string {
  return z.drosselHerkunft
    ? `Drosselabfluss: ${herkunftname(z.drosselHerkunft)} (F84, Fachfrage AUA-02).`
    : 'Drosselabfluss offen: Die Erlaubnis der Einleitstelle steht aus (F84, Fachfrage AUA-02).';
}

const herkunft = (h: string | null | undefined) => (h ? herkunftname(h) : null);

const SPALTEN: Spalte<AussenRueckhaltungszeile>[] = [
  { schluessel: 'einleitstelle', titel: 'Einleitstelle', immerSichtbar: true },
  { schluessel: 'vorhandenM3', titel: 'vorhanden', einheit: 'm³', art: 'zahl', nachkomma: 1 },
  {
    schluessel: 'drosselabflussLs', titel: 'Drosselabfluss', einheit: 'l/s', art: 'zahl', nachkomma: 1,
    wert: (z) => z.drosselabflussLs,
    inhalt: (z) => (z.drosselabflussLs === null
      ? <span data-drossel="offen"><StatusBadge zustand="warnung" text="offen" titel={drosselGrund(z)} klein /></span>
      : formatiereZahl(z.drosselabflussLs, 1)),
  },
  { schluessel: 'obergrenzeFlaecheM2', titel: 'Obergrenze Fläche', einheit: 'm²', art: 'zahl', nachkomma: 0 },
  { schluessel: 'wiederkehrzeitJahre', titel: 'T', einheit: 'a', art: 'zahl', nachkomma: 0 },
  { schluessel: 'zuschlagsfaktorFz', titel: 'f_z', art: 'zahl', nachkomma: 2 },
  { schluessel: 'erforderlichOhneDrosselM3', titel: 'erforderlich ohne Drossel', einheit: 'm³', art: 'zahl', nachkomma: 1 },
  { schluessel: 'erforderlichM3', titel: 'erforderlich', einheit: 'm³', art: 'zahl', nachkomma: 1 },
  { schluessel: 'notwendigerDrosselLs', titel: 'notwendige Drossel', einheit: 'l/s', art: 'zahl', nachkomma: 1 },
  { schluessel: 'tragfaehigeFlaecheM2', titel: 'tragfähige Fläche', einheit: 'm²', art: 'zahl', nachkomma: 0 },
  { schluessel: 'massgebendeDauerMin', titel: 'maßgebende Dauer', einheit: 'min', art: 'zahl', nachkomma: 0 },
  { schluessel: 'regenspendeLsHa', titel: 'Regenspende', einheit: 'l/(s·ha)', art: 'zahl', nachkomma: 1 },
  { schluessel: 'deckungProzent', titel: 'Deckung', einheit: '%', art: 'zahl', nachkomma: 0 },
  {
    schluessel: 'bewertung', titel: 'Bewertung', art: 'status',
    wert: (z) => rueckhaltungStatus(z.bewertung).text,
    inhalt: (z) => <StatusBadge {...rueckhaltungStatus(z.bewertung)} klein />,
  },
  { schluessel: 'befund', titel: 'Befund', wert: (z) => z.befund },
  { schluessel: 'norm', titel: 'Norm', wert: (z) => z.norm, versteckt: true },
  { schluessel: 'gerechnetAm', titel: 'gerechnet', wert: (z) => zeitpunkt(z.gerechnetAm) || null },
];

/** Eine Zeile der Annahmen: welche Eingangsgröße einer Einleitstelle woher kommt (S1.6). */
interface Annahmezeile {
  schluessel: string;
  einleitstelle: string;
  groesse: string;
  wert: number | null;
  einheit: string;
  herkunft: string | null;
}

function annahmen(zeilen: AussenRueckhaltungszeile[]): Annahmezeile[] {
  return zeilen.flatMap((z): Annahmezeile[] => [
    {
      schluessel: `${z.id}:vorhanden`, einleitstelle: z.einleitstelle, groesse: 'vorhandenes Volumen',
      wert: z.vorhandenM3, einheit: 'm³', herkunft: herkunft(z.vorhandenHerkunft),
    },
    {
      schluessel: `${z.id}:drossel`, einleitstelle: z.einleitstelle, groesse: 'Drosselabfluss',
      wert: z.drosselabflussLs, einheit: 'l/s', herkunft: herkunft(z.drosselHerkunft),
    },
    {
      schluessel: `${z.id}:flaeche`, einleitstelle: z.einleitstelle, groesse: 'Obergrenze der Fläche',
      wert: z.obergrenzeFlaecheM2, einheit: 'm²', herkunft: herkunft(z.flaecheHerkunft),
    },
  ]);
}

const ANNAHMESPALTEN: Spalte<Annahmezeile>[] = [
  { schluessel: 'einleitstelle', titel: 'Einleitstelle', immerSichtbar: true },
  { schluessel: 'groesse', titel: 'Größe' },
  { schluessel: 'wert', titel: 'Wert', art: 'zahl', nachkomma: 1 },
  { schluessel: 'einheit', titel: 'Einheit' },
  {
    schluessel: 'herkunft', titel: 'Herkunft', wert: (a) => a.herkunft,
    inhalt: (a) => (a.herkunft ?? <StatusBadge zustand="warnung" text="ohne Herkunft" klein />),
  },
];

export function AussenRueckhaltung({ api, projektId }: { api: ApiClient; projektId: string }) {
  const [zeilen, setZeilen] = useState<AussenRueckhaltungszeile[] | null>(null);
  const [fehler, setFehler] = useState<string | null>(null);
  const [versuch, setVersuch] = useState(0);
  // Der Versickerungslauf: `undefined` = Abruf läuft, `null` = noch nicht gerechnet.
  const [lauf, setLauf] = useState<AussenBerechnungslauf | null | undefined>(undefined);

  useEffect(() => {
    let lebt = true;
    setFehler(null);
    setZeilen(null);
    api.aussenRueckhaltung(projektId)
      .then((z) => { if (lebt) setZeilen(z); })
      .catch((e) => { if (lebt) setFehler(e instanceof Error ? e.message : String(e)); });
    return () => { lebt = false; };
  }, [api, projektId, versuch]);

  useEffect(() => {
    let lebt = true;
    setLauf(undefined);
    api.aussenBerechnung(projektId, 'versickerung')
      .then((l) => { if (lebt) setLauf(l); })
      .catch(() => { if (lebt) setLauf(null); });
    return () => { lebt = false; };
  }, [api, projektId]);

  const stand = useBerichtsstand(api, projektId, 'versickerung');
  const bericht = berichtZiel(api, projektId, 'versickerung', stand);

  const vorbehalte: Vorbehalt[] = [];
  const frage = (zeilen && zeilen.length > 0) || lauf ? aussenFachvorbehalt('versickerung') : null;
  if (frage) vorbehalte.push(frage);
  if (lauf?.standVeraltet) {
    vorbehalte.push({
      art: 'veraltet',
      text: `Der Versickerungslauf ist vom ${zeitpunkt(lauf.gerechnetAm)}, die jüngste Außenaufnahme vom `
        + `${zeitpunkt(lauf.modellstand)}. Die Rückhaltung gilt für einen Modellstand, den es nicht mehr gibt.`,
    });
  }

  useAussenKopf({
    meta: lauf ? <Rechenstand lauf={lauf} veraltet={lauf.standVeraltet} />
      : lauf === null ? <Rechenstand lauf={null} /> : undefined,
    ziele: [
      lauf ? bericht : { ...bericht, gesperrt: { grund: OHNE_LAUF } },
      {
        // Die Rückhaltung steht in der Versickerungsmappe (TS11) - ohne Lauf gesperrt mit Grund.
        art: 'excel', beschriftung: 'Excel Versickerung', laden: () => api.aussenBerechnungExcel(projektId, 'versickerung'),
        gesperrt: lauf ? undefined : { grund: OHNE_LAUF },
      },
    ],
    vorbehalte,
  }, [api, projektId, lauf, stand, zeilen && zeilen.length > 0]);

  if (fehler) {
    return (
      <ErrorState
        titel="Die Rückhaltung konnte nicht geladen werden."
        meldung={fehler}
        beiErneutVersuchen={() => setVersuch((v) => v + 1)}
      />
    );
  }

  if (!zeilen) return <LoadingState text="Rückhaltung wird abgerufen …" />;

  if (zeilen.length === 0) {
    return (
      <EmptyState
        titel="Keine Einleitstelle mit Rückhaltung gerechnet."
        erklaerung={'Die Rückhaltung nach DWA-A 117 entsteht mit dem Versickerungslauf (Berechnungen, Bereich '
          + '„Versickerung und Rückhaltung“) - je Regenwasser-Einleitstelle eine Zeile.'}
      />
    );
  }

  const offen = zeilen.filter((z) => z.drosselabflussLs === null).length;
  const unterdeckt = zeilen.filter((z) => z.bewertung === 'unterdeckt').length;

  return (
    <>
      <KpiReihe>
        <KpiCard titel="Einleitstellen" wert={zeilen.length} nachkomma={0} />
        <KpiCard
          titel="Drossel offen" wert={offen} nachkomma={0}
          zustand={offen > 0 ? 'warnung' : 'gut'}
          fussnote="ohne genehmigten Drosselabfluss (F84, AUA-02)"
        />
        <KpiCard titel="unterdeckt" wert={unterdeckt} nachkomma={0} zustand={unterdeckt > 0 ? 'fehler' : 'gut'} />
      </KpiReihe>

      <ContentSection
        titel="Rückhaltung je Einleitstelle"
        beschreibung={'Erforderliches Rückhaltevolumen nach DWA-A 117 gegen das vorhandene - mit und ohne Drossel. '
          + 'Ein offener Drosselabfluss ist kein Pflegerückstand: Er wartet auf die Erlaubnis der Einleitstelle.'}
      >
        <DataTable<AussenRueckhaltungszeile>
          name="Rückhaltung"
          spalten={SPALTEN}
          zeilen={zeilen}
          schluessel={(z) => z.id}
          ausfuhrDateiname="aussen-rueckhaltung"
          leer={<EmptyState klein titel="Keine Einleitstelle." />}
          dicht
        />
      </ContentSection>

      <ContentSection
        titel="Annahmen und Vorgaben"
        beschreibung="Woher die Eingangsgrößen je Einleitstelle kommen. Ohne Herkunft ist ein Wert nicht belegt."
        aufklappbar
        standardOffen
      >
        <DataTable<Annahmezeile>
          name="Herkunft der Eingangsgrößen"
          spalten={ANNAHMESPALTEN}
          zeilen={annahmen(zeilen)}
          schluessel={(a) => a.schluessel}
          ausfuhrDateiname="aussen-rueckhaltung-annahmen"
          leer={<EmptyState klein titel="Keine Eingangsgrößen." />}
          dicht
        />
      </ContentSection>
    </>
  );
}

/** Die Unterseite im Rahmen der Außenanlagen (Route `aussenanlagen/rueckhaltung`). */
export function AussenRueckhaltungSeite() {
  const { api, projektId } = useOutletContext<AussenanlagenKontext>();
  return <AussenRueckhaltung api={api} projektId={projektId} />;
}
